import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NewAnalyticsApi from 'api/new-analytics';
import type { AnalyticsPlatform } from 'api/new-analytics/types';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import { useAnalyticsContext } from 'app/modules/analytics/context/AnalyticsContext';
import { useAnalyticsFiltersStore } from 'app/modules/analytics/store/analyticsFiltersStore';
import { pageTypeToAnalyticsPlatform } from 'app/modules/analytics/modules/content/utils/analyticsPlatformToPageType';

export interface DownloadSectionCsvOptions {
  section: 'performance' | 'content';
  platform?: AnalyticsPlatform;
  pageIds: number[];
  compareSince?: string;
  compareUntil?: string;
}

function buildAnalyticsExportFilename(
  section: 'performance' | 'content',
  since: string,
  until: string,
  platform?: AnalyticsPlatform,
): string {
  const segment = platform ? `${platform}-${section}` : section;
  return `analytics-${segment}-${since}-${until}.csv`;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export function useAnalyticsSectionExport() {
  const { t } = useTranslation();
  const toast = useToast();
  const { timezone } = useAnalyticsContext();
  const startDate = useAnalyticsFiltersStore((state) => state.startDate);
  const endDate = useAnalyticsFiltersStore((state) => state.endDate);
  const [isExporting, setIsExporting] = useState(false);

  const downloadSectionCsv = useCallback(
    async (options: DownloadSectionCsvOptions) => {
      const { pageIds, section, platform, compareSince, compareUntil } =
        options;
      if (pageIds.length === 0) {
        return;
      }
      setIsExporting(true);
      try {
        const since = startDate.format('YYYY-MM-DD');
        const until = endDate.format('YYYY-MM-DD');
        const blob = await NewAnalyticsApi.exportData({
          page_ids: pageIds,
          since,
          until,
          timezone,
          section,
          platform,
          compare_since: compareSince,
          compare_until: compareUntil,
        });
        const filename = buildAnalyticsExportFilename(
          section,
          since,
          until,
          platform,
        );
        triggerBlobDownload(blob, filename);
      } catch (e) {
        const err = e as ApiClientError;
        toast(err?.message ?? t('somethingWentWrong'), 'error');
      } finally {
        setIsExporting(false);
      }
    },
    [endDate, startDate, t, timezone, toast],
  );

  const downloadPerformanceCsvForPage = useCallback(
    (
      page: { id: number; type: number } | undefined,
      period?: { compare_since?: string; compare_until?: string },
    ) => {
      if (!page) {
        return;
      }
      const platform = pageTypeToAnalyticsPlatform(page.type);
      if (!platform) {
        return;
      }
      void downloadSectionCsv({
        section: 'performance',
        platform,
        pageIds: [page.id],
        compareSince: period?.compare_since,
        compareUntil: period?.compare_until,
      });
    },
    [downloadSectionCsv],
  );

  const downloadPerformanceCsvForGroupPlatform = useCallback(
    (
      page: { id: number; type: number } | undefined,
      pageIds: number[],
      period?: { compare_since?: string; compare_until?: string },
    ) => {
      if (!page || pageIds.length === 0) {
        return;
      }
      const platform = pageTypeToAnalyticsPlatform(page.type);
      if (!platform) {
        return;
      }
      void downloadSectionCsv({
        section: 'performance',
        platform,
        pageIds,
        compareSince: period?.compare_since,
        compareUntil: period?.compare_until,
      });
    },
    [downloadSectionCsv],
  );

  const downloadContentCsvForPage = useCallback(
    (page: { id: number; type: number } | undefined) => {
      if (!page) {
        return;
      }
      const platform = pageTypeToAnalyticsPlatform(page.type);
      if (!platform) {
        return;
      }
      void downloadSectionCsv({
        section: 'content',
        platform,
        pageIds: [page.id],
      });
    },
    [downloadSectionCsv],
  );

  return {
    downloadSectionCsv,
    downloadContentCsvForPage,
    downloadPerformanceCsvForPage,
    downloadPerformanceCsvForGroupPlatform,
    isExporting,
  };
}
